import documentsSigningConfig from '../sample-config/documentsSigningConfig.json';

import { SEND_SLACK_NOTIFICATION, UPSERT_FILE } from '../../api';
import { apiURL } from '../../config';
import { v4 as uuidv4 } from 'uuid';
import { Alert, AlertTitle, Button, CircularProgress, Grid, Icon, Typography } from '@mui/material';
import { FormikProps, useFormikContext } from 'formik';
import { useEffect, useState, useRef, useContext } from 'react';
import { Element } from '../Element';
import { TaskStatuses } from '../../api/types';
import { SectionType } from '../types';
import { Constants } from '../../constants';
import { Document, Page, pdfjs } from 'react-pdf';
import highlightCover from '../../assets/images/highlightCover.png';
import axios from 'axios';
import { useEffectDebugger, useFormikDocsUpload } from '../../helpers/customHooks';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { CommonDataContext } from '../../context';
import {
  getAvailableToSignPersonsFieldOptions,
  getOwnerFullNameByWhoIsPresentToSignKey,
  getURLParameter,
} from '../../helpers';
import { newRelicNoticeError, newRelicAddPageAction } from '../../tracking/newrelic';
import _ from 'lodash';
import { useMutation } from '@apollo/client';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

/* TODO TO Improve
 * Addendum question not hardcoded
 *
 */

const DocumentsSigningSection = ({
  config,
  locale,
  externalData,
}: {
  config: SectionType;
  locale: string;
  externalData: any;
}) => {
  const { containerMaxWidth, setContainerMaxWidth, isMobileView, accountTasksTemplates } =
    useContext(CommonDataContext);

  const [sendSlackNotification] = useMutation(SEND_SLACK_NOTIFICATION);

  const formik: FormikProps<any> = useFormikContext();
  const [docsUpload, setDocsUpload] = useFormikDocsUpload();
  const requireSigning = _.get(config, `config.requireSigning`);
  const tasksAutoCreation = _.get(config, `config.tasksAutoCreation`);
  const templateId = _.get(config, `config.pdfTemplateId`);
  //@ts-ignore
  const templateConfiguration = _.get(documentsSigningConfig, templateId);
  const fieldsToFillIn = _.reject(templateConfiguration.fields, 'toSkipForNow');
  _.forEach(fieldsToFillIn, (field) => {
    _.set(field, 'config.required', requireSigning);
  });
  const acceptAddendumField = _.find(config.fields, (f) => f.config.fieldName === 'addendum');
  const whoIsPresentToSignField = _.find(
    config.fields,
    (f) => f.config.fieldName === 'whoIsPresentToSignDocument',
  );
  const useMultipleSigning = !!whoIsPresentToSignField;

  const pdfContainerRef = useRef<HTMLInputElement>(null);
  const [currentFieldIdx, setCurrentFieldIdx] = useState(0);
  const [currentSignorIdx, setCurrentSignorIdx] = useState(0);

  const [isSigningAccepted, setIsSigningAccepted] = useState(
    _.get(formik, `values.addendum`) === 'accept',
  );
  const [isAddendumSigned, setIsAddendumSigned] = useState(
    !!_.get(formik, `values.isAddendumSigned`),
  );

  const [isJustFinishedSigning, setIsJustFinishedSigning] = useState(false);
  const initialPayload = {
    date: '',
  };
  const [isPDFLoading, setIsPDFLoading] = useState(false);
  const [isFileUploadLoading, setIsFileUploadLoading] = useState(false);
  const [isSigningFailed, setIsSigningFailed] = useState(false);
  const [currentPDFPayload, setCurrentPDFPayload] = useState<any>({ ...initialPayload });
  const [pdfContainerWidth, setPDFContainerWidth] = useState(535);
  const [pdfData, setPdfData] = useState();
  const [currentSignorFullName, setCurrentSignorFullName] = useState<string | undefined>();
  const [sendDocumentsSignaturesTasks, setSendDocumentsSignaturesTasks] = useState(
    _.get(formik, `values.sendDocumentsSignaturesTasks`, {}),
  );

  const isWhoIsPresentToSignDocumentConfirmed = _.get(
    formik,
    `values.isWhoIsPresentToSignDocumentConfirmed`,
  );

  const sectionHeader = _.get(config, `title.config.displayLabel.${locale}`, '');

  //@ts-ignore
  const onDocumentLoadSuccess = ({ numPages }) => {
    //setNumPages(numPages);
  };

  const getHighlightPayload = (activeIdx: number | null) =>
    _.reduce(
      fieldsToFillIn,
      (acc: any, field: any, idx: number) => ({
        ...acc,
        [field.highlightProp]: idx === activeIdx ? highlightCover : null,
      }),
      {},
    );

  const getFieldValuePayload = (field: any) => {
    const value = _.get(formik, `values.${field.config.fieldName}`, '');
    return {
      [field.config.fieldName]: field.fontSettings ? { ...field.fontSettings, value } : value,
      ...(field.fieldToDuplicateValue && { [field.fieldToDuplicateValue]: value }),
    };
  };

  // highlightIdx: index of the field to highlight on the PDF, null to highlight nothing
  const getPDFPayloadData = (highlightIdx: number | null) => {
    const currentField = fieldsToFillIn[currentFieldIdx];

    const newData = {
      ...getFieldValuePayload(currentField),
      ...getHighlightPayload(highlightIdx),
      date: Date.now(),
    };

    const newPayload = {
      ...currentPDFPayload,
      ...newData,
    };
    return newPayload;
  };

  const downloadPDFHandler = async () => {
    newRelicAddPageAction('DocumentsSigning:downloadPDF');
    try {
      const downloadPDFFromResponse = (response: any) => {
        const fileURL = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = fileURL;
        link.setAttribute('target', '_blank');
        link.setAttribute('download', `addendum_${Date.now()}.pdf`);
        document.body.appendChild(link);
        link.click();
      };
      const response = await requestPDF(initialPayload);
      downloadPDFFromResponse(response);
    } catch (error) {
      console.error(error);
      newRelicNoticeError(error as Error, { action: 'downloadPDFHandler' });
    }
  };

  const requestPDF = async (payloadData: any) => {
    try {
      setIsPDFLoading(true);
      const payload = {
        title: 'Addendum',
        fontSize: 10,
        textColor: '#000000',
        data: payloadData,
      };
      return await axios
        .post(
          `${process.env.REACT_APP_DCR_API_BASE_SERVER_URL}/create-pdf-to-sign?templateId=${templateId}`,
          payload,
          {
            responseType: 'blob',
            timeout: 60000,
          },
        )
        .catch((error) => {
          console.error(error);
          newRelicNoticeError(error as Error, { action: 'requestPDF:axios' });
        });
    } catch (error) {
      console.error(error);
      newRelicNoticeError(error as Error, { action: 'requestPDF' });
    } finally {
      setIsPDFLoading(false);
    }
  };

  // TODO: reload only if payloadData changed
  const requestPDFToRender = async (payloadData: any) => {
    try {
      const response = await requestPDF(payloadData);
      if (response && response.data && response.data.size > 0) {
        const pdfData = response.data;
        setPdfData(pdfData);
        return pdfData;
      }
    } catch (error) {
      console.error(error);
      newRelicNoticeError(error as Error, { action: 'requestPDFToRender' });
    }
  };

  useEffect(() => {
    setContainerMaxWidth(false); // need to make screen wider
    // currentPDFPayload is lost on remount, so a signed document is rebuilt from all field values in formik
    const signedFieldsPayload = isAddendumSigned
      ? _.reduce(
          fieldsToFillIn,
          (acc: any, field: any) => ({ ...acc, ...getFieldValuePayload(field) }),
          {},
        )
      : {};
    const currentPDFPayloadToSet = {
      ...signedFieldsPayload,
      ...getPDFPayloadData(isSigningAccepted && !isAddendumSigned ? currentFieldIdx : null),
    };
    setCurrentPDFPayload(currentPDFPayloadToSet);
    requestPDFToRender(currentPDFPayloadToSet);
    return () => {
      setContainerMaxWidth(Constants.DefaultContainerMaxWidth);
      formik.setFieldError('isAddendumSigned', '');
    };
  }, []);

  useEffect(() => {
    if (containerMaxWidth === false) {
      if (pdfContainerRef.current && pdfContainerRef.current.offsetWidth) {
        setPDFContainerWidth(pdfContainerRef.current.offsetWidth);
      }
    }
  }, [containerMaxWidth]);

  const resetFlow = async (isSigningAccepted: boolean, isFullReset: boolean) => {
    _.forEach(fieldsToFillIn, (field) => {
      formik.setFieldValue(field.config.fieldName, '', false);
    });
    setCurrentFieldIdx(0);
    setIsJustFinishedSigning(false);
    setIsAddendumSigned(false);
    const currentPDFPayloadToSet = isSigningAccepted
      ? { ...initialPayload, [fieldsToFillIn[0].highlightProp]: highlightCover }
      : { ...initialPayload };
    setCurrentPDFPayload(currentPDFPayloadToSet);
    await requestPDFToRender(currentPDFPayloadToSet);
    if (isFullReset) {
      setDocsUpload((docs) => docs.filter((d: any) => d.docsUpload !== 'Addendum'));
    }
  };

  const isFirstAddendumEffectRef = useRef(true);

  useEffectDebugger(
    (changedDeps: any) => {
      const isSigningAccepted = formik.values.addendum === 'accept';
      setIsSigningAccepted(isSigningAccepted);

      const isFirstRun = isFirstAddendumEffectRef.current;
      isFirstAddendumEffectRef.current = false;

      const valueBefore = _.get(changedDeps, 'addendum.before');
      const valueAfter = _.get(changedDeps, 'addendum.after');
      if (!!valueBefore && valueBefore !== valueAfter) {
        resetFlow(isSigningAccepted, true);
      } else if (!isFirstRun && !valueBefore && isSigningAccepted && !isAddendumSigned) {
        // first accept: the mount payload has no highlight yet
        resetFlow(true, false);
      }
    },
    [_.get(formik, `values.addendum`)],
    ['addendum'],
  );

  useEffect(() => {
    if (isSigningAccepted && !isAddendumSigned && requireSigning) {
      formik.setFieldError('isAddendumSigned', 'required');
      return;
    }

    if (useMultipleSigning && !isWhoIsPresentToSignDocumentConfirmed) {
      formik.setFieldError('isAddendumSigned', 'required'); // do not allow to click Next until confirm who is signing
      return;
    }

    formik.setFieldError('isAddendumSigned', '');
  }, [isSigningAccepted, isAddendumSigned, isWhoIsPresentToSignDocumentConfirmed]);

  useEffect(() => {
    formik.setFieldValue(`isAddendumSigned`, isAddendumSigned, false);
  }, [isAddendumSigned]);

  const hasAddendumDocument = _.some(
    docsUpload,
    (d: any) => d.docsUpload === 'Addendum' && !_.isEmpty(d.fileToUpload),
  );

  useEffect(() => {
    // signed state without the uploaded file would let the user continue with accept and no document
    if (isAddendumSigned && !hasAddendumDocument) {
      resetFlow(isSigningAccepted, false);
    }
  }, [isAddendumSigned, hasAddendumDocument]);

  useEffect(() => {
    /*// skip for single signing flow, or if is not confirmed who is signing yet
       if (!useMultipleSigning || !isWhoIsPresentToSignDocumentConfirmed) {
    return;
  } */
    if (useMultipleSigning && isWhoIsPresentToSignDocumentConfirmed) {
      const whoIsPresentToSignKey = getCurrentWhoIsPresentToSignKey();
      setCurrentSignorFullName(
        getOwnerFullNameByWhoIsPresentToSignKey(whoIsPresentToSignKey, formik),
      );
    }
  }, [currentSignorIdx, isWhoIsPresentToSignDocumentConfirmed]);

  useEffect(() => {
    formik.setFieldValue(`sendDocumentsSignaturesTasks`, sendDocumentsSignaturesTasks);
  }, [sendDocumentsSignaturesTasks]);

  const continueHandler = async () => {
    newRelicAddPageAction('DocumentsSigning:continueHandler');
    const currentPDFPayloadToSet = getPDFPayloadData(currentFieldIdx + 1);
    setCurrentPDFPayload(currentPDFPayloadToSet);
    await requestPDFToRender(currentPDFPayloadToSet);
    //setCurrentField(fieldsToFillIn[currentFieldIdx + 1]);
    setCurrentFieldIdx(currentFieldIdx + 1);
  };

  const backHandler = async () => {
    const prevIdx = currentFieldIdx - 1;
    const currentPDFPayloadToSet = { ...currentPDFPayload, ...getHighlightPayload(prevIdx) };
    setCurrentPDFPayload(currentPDFPayloadToSet);
    setCurrentFieldIdx(prevIdx);
    await requestPDFToRender(currentPDFPayloadToSet);
  };

  const finishHandler = async () => {
    newRelicAddPageAction('DocumentsSigning:finishSigning');
    setIsSigningFailed(false);
    const currentPDFPayloadToSet = getPDFPayloadData(null);
    setCurrentPDFPayload(currentPDFPayloadToSet);
    const pdfData = await requestPDFToRender(currentPDFPayloadToSet);
    if (!pdfData) {
      sendSlackNotification({
        variables: {
          message: `CatFi Addendum: Error while generating PDF for signing for formId ${getURLParameter('formId')}.`,
        },
      });
      setIsSigningFailed(true);
      return;
    }

    const uploadedDocument = await uploadFile(pdfData);
    if (!uploadedDocument) {
      setIsSigningFailed(true);
      return;
    }

    setIsAddendumSigned(true);
    if (useMultipleSigning) {
      const isLastSignor =
        currentSignorIdx ===
        _.size(_.get(formik, `values.${whoIsPresentToSignField.config.fieldName}`)) - 1;
      handleSendDocumentsSignaturesTasks(isLastSignor, true);
      setAddendumDataForSignor();
      if (!isLastSignor) {
        resetFlow(true, false);
        setCurrentSignorIdx((prevIdx) => prevIdx + 1);
      }
    }
  };

  const uploadFile = async (pdfData: any) => {
    const fileOfBlob = new File([pdfData], `CatFi Addendum.pdf`);
    const formData = new FormData();
    const fileStringMap = `{"0": ["variables.files.0"]}`;
    formData.append(
      'operations',
      JSON.stringify({
        query: UPSERT_FILE,
        variables: {
          files: [null],
        },
      }),
    );
    formData.append('map', fileStringMap);
    formData.append('0', fileOfBlob);
    setIsFileUploadLoading(true);
    try {
      const response = await fetch(apiURL, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`Upload request failed with status ${response.status}`);
      }
      const responseBody = await response.json();
      const upsertedFile = _.get(responseBody, 'data.upsertFile[0]');
      if (_.isEmpty(upsertedFile)) {
        throw new Error(
          `upsertFile returned no file. Errors: ${JSON.stringify(_.get(responseBody, 'errors'))}`,
        );
      }
      const uploadedDocument = {
        id: uuidv4(),
        fileToUpload: upsertedFile,
        docsUpload: 'Addendum',
        docDescription: _.get(formik, 'values.authorizedSignature'),
      };

      setDocsUpload((docsUpload: any) => {
        return [...docsUpload, uploadedDocument];
      });
      if (externalData?.setUploadedSignedDocument) {
        externalData.setUploadedSignedDocument(uploadedDocument);
      }
      return uploadedDocument;
    } catch (error: any) {
      newRelicNoticeError(error as Error, { action: 'uploadFile:fetch' });
      sendSlackNotification({
        variables: {
          message: `CatFi Addendum: Error while uploading signed addendum file for formId ${getURLParameter('formId')}. Error details: ${error?.message}`,
        },
      });
      return undefined;
    } finally {
      setIsFileUploadLoading(false);
    }
  };

  const getCurrentWhoIsPresentToSignKey = () => {
    const whoIsPresentToSignValue = _.get(
      formik,
      `values.${whoIsPresentToSignField?.config.fieldName}`,
    );
    return whoIsPresentToSignValue[currentSignorIdx];
  };

  const setAddendumDataForSignor = () => {
    const whoIsPresentToSignKey = getCurrentWhoIsPresentToSignKey();
    const formikValuesKey =
      whoIsPresentToSignKey === Constants.WhoIsPresentToSignPOCKey
        ? ``
        : `owners[${whoIsPresentToSignKey}].`;
    formik.setFieldValue(`${formikValuesKey}addendum`, 'accept');
    formik.setFieldValue(`${formikValuesKey}isAddendumSigned`, true);
  };

  const handleSendDocumentsSignaturesTasks = (isLastSignor: boolean, isUploadSuccess: boolean) => {
    if (!tasksAutoCreation) {
      return;
    }
    const whoIsPresentToSignKey = getCurrentWhoIsPresentToSignKey();
    setSendDocumentsSignaturesTasks((prev: any) => ({
      ...prev,
      [whoIsPresentToSignKey]: isUploadSuccess ? TaskStatuses.Complete : TaskStatuses.Open,
    }));
    if (isLastSignor) {
      const isIndividualApp = _.get(externalData, 'isIndividualApp');
      const whoIsPresentToSignAvailableKeys = _.map(
        getAvailableToSignPersonsFieldOptions(formik, isIndividualApp),
        'optionValue',
      );
      const nonSignors = _.differenceBy(
        whoIsPresentToSignAvailableKeys,
        _.get(formik, `values.${whoIsPresentToSignField?.config.fieldName}`),
      );
      const nonSignorsConfig = _.reduce(
        nonSignors,
        (result, key) => ({ ...result, [key]: TaskStatuses.Open }),
        {},
      );
      setSendDocumentsSignaturesTasks((prev: any) => ({ ...prev, ...nonSignorsConfig }));
    }
  };

  const handleWhoIsPresentToSignConfirm = () => {
    formik.setFieldValue('isWhoIsPresentToSignDocumentConfirmed', true);
  };

  const getDownloadPDFButtonLayoyt = () => {
    return (
      <Grid item container xs={12} justifyContent={'flex-end'}>
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={downloadPDFHandler}
          endIcon={<Icon>download</Icon>}
        >
          Download PDF
        </Button>
      </Grid>
    );
  };

  const getInputsWizardLayoyt = () => {
    return (
      <>
        {fieldsToFillIn.map(
          (field: any, index: number) =>
            index === currentFieldIdx && (
              <Element item={field} key={field.config.fieldName} locale={locale} />
            ),
        )}
        <Grid item xs={6}>
          {currentFieldIdx !== 0 && (
            <Button
              variant="outlined"
              color="primary"
              disabled={isPDFLoading || isFileUploadLoading}
              onClick={backHandler}
            >
              Back
            </Button>
          )}
        </Grid>
        <Grid item container xs={6} justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            disabled={
              isPDFLoading ||
              isFileUploadLoading ||
              !!formik.errors[fieldsToFillIn[currentFieldIdx]?.config.fieldName]
            }
            onClick={() => {
              currentFieldIdx === fieldsToFillIn.length - 1 ? finishHandler() : continueHandler();
            }}
          >
            {currentFieldIdx === fieldsToFillIn.length - 1 ? (
              <span>Finish</span>
            ) : (
              <span>Continue</span>
            )}
          </Button>
        </Grid>
      </>
    );
  };

  const getWhoIsPresentToSignLayoyt = () => {
    return (
      <>
        <Element
          item={whoIsPresentToSignField as any}
          locale={locale}
          externalData={externalData}
        />
        <Grid item container xs={12} justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            disabled={_.isEmpty(
              _.get(formik, `values.${whoIsPresentToSignField?.config.fieldName}`),
            )}
            onClick={handleWhoIsPresentToSignConfirm}
          >
            Confirm
          </Button>
        </Grid>
      </>
    );
  };

  const isNeedToRenderWhoIsPresentToSign = useMultipleSigning
    ? !isWhoIsPresentToSignDocumentConfirmed && !isAddendumSigned
    : false;

  const isNeedToRenderAcceptAddendumField = useMultipleSigning
    ? isWhoIsPresentToSignDocumentConfirmed && !isAddendumSigned
    : true;

  const isNeedToRenderInputsWizard = useMultipleSigning
    ? isWhoIsPresentToSignDocumentConfirmed && isSigningAccepted && !isAddendumSigned
    : isSigningAccepted && !isAddendumSigned;

  const isNeedToRenderSigningForLabel =
    useMultipleSigning &&
    isWhoIsPresentToSignDocumentConfirmed &&
    currentSignorFullName &&
    !isAddendumSigned;
  return (
    <Grid container item xs={12} spacing={2}>
      <Grid item xs={12}>
        <h3 style={config.title.config}>{sectionHeader}</h3>
      </Grid>
      <Grid item container md={4} sm={12} spacing={2} style={{ alignSelf: 'flex-start' }}>
        {isNeedToRenderWhoIsPresentToSign && getWhoIsPresentToSignLayoyt()}

        {isNeedToRenderSigningForLabel && (
          <Typography style={{ paddingLeft: 16, marginBottom: 20 }}>
            Signing For <strong>{currentSignorFullName}</strong>
          </Typography>
        )}

        {isNeedToRenderAcceptAddendumField && (
          <Element item={acceptAddendumField as any} locale={locale} externalData={externalData} />
        )}

        {isNeedToRenderInputsWizard && getInputsWizardLayoyt()}

        {isSigningFailed && (
          <Grid item xs={12}>
            <Alert severity="error">
              We could not save the signed document. Please click <b>"Finish"</b> to try again.
            </Alert>
          </Grid>
        )}

        {isAddendumSigned && (
          <Grid item xs={12}>
            <Alert severity="success">
              <AlertTitle>
                <b>Success</b>
              </AlertTitle>
              <Typography>
                You have successfully completed the <b>Addendum</b>. Please click the <b>"Next"</b>{' '}
                button at the bottom of this page to continue with the <b>application process</b>.
              </Typography>
            </Alert>
          </Grid>
        )}
      </Grid>
      <Grid
        item
        container
        md={8}
        sm={12}
        spacing={2}
        ref={pdfContainerRef}
        style={{ minHeight: '450px' }}
      >
        {isPDFLoading && <CircularProgress style={{ margin: '60px auto' }} />}
        {!isPDFLoading && pdfData && (
          <>
            {!isMobileView && getDownloadPDFButtonLayoyt()}
            <Grid item container xs={12}>
              <Document file={pdfData} onLoadSuccess={onDocumentLoadSuccess}>
                <Page pageNumber={1} renderTextLayer={false} scale={1} width={pdfContainerWidth} />
              </Document>
            </Grid>
            {isMobileView && getDownloadPDFButtonLayoyt()}
          </>
        )}
      </Grid>
    </Grid>
  );
};

export { DocumentsSigningSection };
